import {
  call,
  cancel,
  delay,
  fork,
  put,
  select,
  take,
  takeLatest,
} from 'redux-saga/effects'
import { eventChannel } from 'redux-saga'
import { v4 as uuid } from 'uuid'
import actionTypes from '../actionTypes'
import getSessionRequest from '../selectors/getSessionRequest'
import setRandomSessionIdentifier from '../actions/setRandomSessionIdentifier'
import setAccessToken from '../actions/setAccessToken'
import getAccessToken from '../selectors/getAccessToken'
import getRandomSessionIdentifier from '../selectors/getRandomSessionIdentifier'
import getAuthService from '../../getAuthService'
import signOut from '../actions/signOut'
import setAuthError from '../actions/setAuthError'
import { acceptAction, rejectAction } from '../../reduxPromiseHelpers'

const createIframeChannel = (client) => eventChannel((emit) => {
  const onMessage = (event) => {
    if (event && event.data && event.origin === getAuthService(client)) {
      emit(event.data)
    }
  }

  window.addEventListener('message', onMessage)

  return () => {
    window.removeEventListener('message', onMessage)
  }
})

const addRequestHandler = (postMessageToken, requestHandlers) => new Promise((accept, reject) => {
  const timer = setTimeout(() => {
    if (!(postMessageToken in requestHandlers)) {
      return
    }
    const handler = requestHandlers[postMessageToken]

    handler.reject(
      new Error(`postMessage ${postMessageToken} has timed out while waiting for response`),
    )
  }, 15000)
  // eslint-disable-next-line no-param-reassign
  requestHandlers[postMessageToken] = {
    accept: (...args) => {
      if (!(postMessageToken in requestHandlers)) {
        return
      }
      // eslint-disable-next-line no-param-reassign
      delete requestHandlers[postMessageToken]
      clearTimeout(timer)
      accept(...args)
    },
    reject: (...args) => {
      if (!(postMessageToken in requestHandlers)) {
        return
      }
      // eslint-disable-next-line no-param-reassign
      delete requestHandlers[postMessageToken]
      clearTimeout(timer)
      // eslint-disable-next-line prefer-promise-reject-errors
      reject(...args)
    },
  }
})

const requestAccessToken = (iframe, client, rsi, requestHandlers) => {
  const postMessageToken = uuid()
  const promise = addRequestHandler(postMessageToken, requestHandlers)
  iframe.contentWindow.postMessage({
    id: postMessageToken,
    type: 'requestAccessToken',
    payload: {
      rsi,
      client,
    },
  }, getAuthService(client))
  return promise
}

const startSession = (iframe, client, ids, requestHandlers) => {
  const postMessageToken = uuid()
  const promise = addRequestHandler(postMessageToken, requestHandlers)
  iframe.contentWindow.postMessage({
    id: postMessageToken,
    type: 'startSession',
    payload: {
      ...ids,
      client,
    },
  }, getAuthService(client))
  return promise
}

const doSignOut = (iframe, client, requestHandlers) => {
  const postMessageToken = uuid()
  const promise = addRequestHandler(postMessageToken, requestHandlers)
  iframe.contentWindow.postMessage({
    id: postMessageToken,
    type: 'endSession',
    payload: {
      client,
    },
  }, getAuthService(client))
  return promise
}

const onIframeMount = function* onIframeMount() {
  let iframe
  let client
  const requestHandlers = {}

  yield fork(function* messageListener() {
    const startAction = yield take(actionTypes.START)
    const listener = yield call(createIframeChannel, startAction.payload.client)
    while (true) {
      const message = yield take(listener)

      if (!message.id || !(message.id in requestHandlers)) {
        // eslint-disable-next-line no-console
        console.error('received message that is incomplete or has no registered requestHandler', message)
      }

      if (message.error) {
        requestHandlers[message.id].reject(message.error)
      } else if (message.data) {
        requestHandlers[message.id].accept(message.data)
      }
    }
  })

  yield fork(function* listenForSessionRequestVerification() {
    yield takeLatest(actionTypes.VERIFY_SESSION_REQUEST, function* verifySessionRequest() {
      try {
        const sessionRequest = yield select(getSessionRequest)
        if (iframe) {
          const result = yield startSession(iframe, client, { adminSessionRequest: sessionRequest.id }, requestHandlers)
          yield put(setRandomSessionIdentifier(result.rsi))
          const accessTokenResult = yield requestAccessToken(iframe, client, result.rsi, requestHandlers)
          yield put(setAccessToken(accessTokenResult.accessToken))
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error(e)
        yield put(signOut())
      }
    })
  })
  yield fork(function* listenForTransferTokenVerification() {
    yield takeLatest(actionTypes.VERIFY_TRANSFER_TOKEN, function* verifySessionRequest(action) {
      try {
        const transferToken = action.payload
        if (iframe) {
          const result = yield startSession(iframe, client, { transferToken }, requestHandlers)
          yield put(setRandomSessionIdentifier(result.rsi))
          const accessTokenResult = yield requestAccessToken(iframe, client, result.rsi, requestHandlers)
          yield put(setAccessToken(accessTokenResult.accessToken))
          acceptAction(action)
        }
      } catch (e) {
        rejectAction(action, e)
        // eslint-disable-next-line no-console
        console.error(e)
        yield put(signOut())
      }
    })
  })
  yield fork(function* listenForIFrameStart() {
    yield takeLatest(actionTypes.START, function* start(action) {
      try {
        iframe = action.payload.iframeRef()
        client = action.payload.client

        const accessToken = yield select(getAccessToken)
        const rsi = yield select(getRandomSessionIdentifier)
        if (!accessToken && rsi) {
          const accessTokenResult = yield requestAccessToken(iframe, client, rsi, requestHandlers)
          yield put(setAccessToken(accessTokenResult.accessToken))
        }
      } catch (error) {
        if (error.status === 401) {
          yield put(signOut())
          yield put(setAuthError(null))
        } else {
          yield put(setAuthError(error.message))
        }
      }
    })
  })
  yield fork(function* listenForSignout() {
    yield takeLatest(actionTypes.SIGN_OUT, function* start() {
      try {
        if (iframe) {
          yield doSignOut(iframe, client, requestHandlers)
        }
        // eslint-disable-next-line no-empty
      } catch (e) {
      }
    })
  })

  function* refreshAccessTokenTask() {
    yield take(actionTypes.SET_ACCESS_TOKEN)
    while (true) {
      yield delay(20000) // only refresh access token every 20 seconds
      const rsi = yield select(getRandomSessionIdentifier)
      try {
        const accessTokenResult = yield requestAccessToken(iframe, client, rsi, requestHandlers)
        yield put(setAccessToken(accessTokenResult.accessToken))
      } catch (error) {
        // Check if the error is a 401, then signout, otherwise show error somewhere
        if (error.status === 401) {
          yield put(setAuthError(null))
        } else {
          yield put(setAuthError(error.message))
        }
      }
    }
  }

  while (true) {
    const taskInstance = yield fork(refreshAccessTokenTask)
    yield take(actionTypes.SIGN_OUT)
    yield cancel(taskInstance)
  }
}

export default onIframeMount
